import { useState, useRef, useEffect } from 'react';
import type { ExportFormat } from '@/utils/export';
import { logFrontendActivity } from '@/api/activityLog';

export type ExportScope = 'all' | 'filtered';

interface Props {
  onExport: (format: ExportFormat, scope: ExportScope) => void | Promise<void>;
  hasFilters?: boolean; // whether any active filters are applied
  /** Label for what's being exported, e.g. 'families', 'certificates' — used in the activity log entry. */
  entityType?: string;
}

const FORMATS: { fmt: ExportFormat; icon: string; label: string; sub: string }[] = [
  { fmt: 'csv',   icon: '📄', label: 'CSV',        sub: 'Opens in Excel / Sheets' },
  { fmt: 'excel', icon: '📊', label: 'Excel (.xls)', sub: 'Styled with headers'   },
  { fmt: 'pdf',   icon: '🖨',  label: 'PDF',        sub: 'Print / Save as PDF'     },
];

export default function ExportButton({ onExport, hasFilters = false, entityType }: Props) {
  const [open,      setOpen]      = useState(false);
  const [exporting, setExporting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleExport = async (fmt: ExportFormat, scope: ExportScope) => {
    setOpen(false);
    setExporting(true);
    const actionType = fmt === 'pdf' ? 'PRINT' : 'EXPORT';
    logFrontendActivity(actionType, `${fmt.toUpperCase()} export (${scope})`, entityType);
    try { await onExport(fmt, scope); } finally { setExporting(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={exporting}
        className="btn-secondary flex items-center gap-1.5 !px-3"
      >
        {exporting ? (
          <span className="text-xs">Exporting…</span>
        ) : (
          <>
            <span className="text-xs">↓ Export</span>
            <span className="text-[10px] opacity-60">▾</span>
          </>
        )}
      </button>

      {open && !exporting && (
        <div className="absolute right-0 mt-1 w-52 bg-white border border-grid-border shadow-lg rounded z-30">
          {/* All Records section */}
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-grid-border">
            All Records
          </div>
          {FORMATS.map(({ fmt, icon, label, sub }) => (
            <button
              key={`all-${fmt}`}
              onClick={() => handleExport(fmt, 'all')}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-navy font-georgia hover:bg-teal hover:text-white transition-colors"
            >
              <span className="text-base w-5 text-center">{icon}</span>
              <div className="text-left">
                <div className="text-xs font-semibold">{label}</div>
                <div className="text-[10px] opacity-70">{sub}</div>
              </div>
            </button>
          ))}

          {/* Filtered Records section — only shown when filters are active */}
          {hasFilters && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-teal uppercase tracking-wider bg-teal/5 border-t border-b border-grid-border">
                Filtered Records ✓
              </div>
              {FORMATS.map(({ fmt, icon, label, sub }) => (
                <button
                  key={`filtered-${fmt}`}
                  onClick={() => handleExport(fmt, 'filtered')}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-navy font-georgia hover:bg-teal hover:text-white transition-colors"
                >
                  <span className="text-base w-5 text-center">{icon}</span>
                  <div className="text-left">
                    <div className="text-xs font-semibold">{label}</div>
                    <div className="text-[10px] opacity-70">{sub} · filtered</div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
